import React, {useEffect, useRef, useState} from 'react';
import {
    ExportDropdownChevron,
    ExportDropdownMeta,
    ExportDropdownPanel,
    ExportDropdownTriggerButton,
    ExportDropdownWrapper,
    ExportFormatCheckbox,
    ExportFormatConfirmButton
} from './ExportDropdown.styled';
import {useTranslation} from "react-i18next";
import {Divider} from "@mui/material";
import {OcrBatchFileResponse, OcrFileExportFormat, OcrProcessedFile} from "../../../models/ocr.models";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {theme} from "../../../theme";
import {LocalDocumentExportFormat} from "../../../models/local-documents.models";

type ExportFormat = OcrFileExportFormat | LocalDocumentExportFormat;

const FORMAT_LABEL_KEYS: Record<ExportFormat, string> = {
    TXT: 'ocr.checkbox.exportTxt',
    DOCX: 'ocr.checkbox.exportDocx',
    PDF: 'ocr.checkbox.exportPdf',
    XLSX: 'ocr.checkbox.exportXlsx',
};

type ExportableFile = OcrProcessedFile | OcrBatchFileResponse;

interface ExportDropdownProps<T extends ExportFormat> {
    files: ExportableFile[];
    availableFormats: T[];
    onExport: ({files, format}: {files: ExportableFile[]; format: T}) => Promise<void>;
    onExportComplete?: () => void;
    disabled?: boolean;
}

const ExportDropdown = <T extends ExportFormat>({ files, availableFormats, onExport, onExportComplete, disabled = false }: ExportDropdownProps<T>) => {
    const { t } = useTranslation();

    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const initialSetup = () => availableFormats.reduce<Record<ExportFormat, boolean>>(
        (acc, format) => {
            acc[format] = false;
            return acc;
        },
        {} as Record<ExportFormat, boolean>
    );

    const [exportFormats, setExportFormats] = useState(initialSetup());

    const anyFormatSelected = Object.values(exportFormats).some(selected => selected);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handler);

        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const toggleExportFormat = (format: ExportFormat) => {
        setExportFormats(prev => ({ ...prev, [format]: !prev[format] }));
    };

    const handleDownload = async () => {
        if (!files.length || !anyFormatSelected) return;

        for (const format of availableFormats) {
            if (exportFormats[format]) {
                await onExport({ files, format });
            }
        }

        setExportFormats(initialSetup());
        if (onExportComplete) {
            onExportComplete();
        }
        setIsOpen(false);
    };

    return (
        <ExportDropdownWrapper ref={wrapperRef}>
            <ExportDropdownTriggerButton
                variant="contained"
                color="primary"
                onClick={() => setIsOpen(prev => !prev)} sx={{ letterSpacing: '1px' }}
                startIcon={<FileDownloadOutlinedIcon />}
                endIcon={<ExportDropdownChevron $open={isOpen} />}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-label={t('ocr.button.export')}
                disabled={disabled}
            >
                {t('ocr.button.export')}
            </ExportDropdownTriggerButton>

            {isOpen && (
                <ExportDropdownPanel role="menu" aria-label={t('ocr.aria.exportDropdown')}>
                    <ExportDropdownMeta>
                        {t('ocr.exportDropdown.selected', {count: files.length})}
                    </ExportDropdownMeta>

                    <Divider sx={{ border: "none", borderTop: `1px solid ${theme.palette.dividers.light}`, margin: "4px 0"}} />

                    {availableFormats.map(format => (
                        <ExportFormatCheckbox key={format} onClick={() => toggleExportFormat(format)}>
                            <input
                                type="checkbox"
                                checked={exportFormats[format]}
                                onChange={() => toggleExportFormat(format)}
                                onClick={e => e.stopPropagation()}
                                aria-label={t(FORMAT_LABEL_KEYS[format]) as string}
                            />

                            {t(FORMAT_LABEL_KEYS[format])}
                        </ExportFormatCheckbox>
                    ))}

                    <ExportFormatConfirmButton
                        variant="outlined"
                        disabled={!anyFormatSelected || files.length === 0}
                        aria-disabled={!anyFormatSelected || files.length === 0}
                        onClick={handleDownload}
                    >
                        {t('ocr.button.downloadExport', {files: files.length > 0 ? `(${files.length})` : ''})}
                    </ExportFormatConfirmButton>
                </ExportDropdownPanel>
            )}
        </ExportDropdownWrapper>
    );
};

export default ExportDropdown;